/**
 * Shared interactive agent hierarchy tree built on ReactFlow.
 * Used by both Admin/AgentTree and MyTeam/DownlineTree.
 *
 * Collapse/expand: every node with sub_agent_count > 0 carries a toggle
 * button.  Already-loaded branches collapse/expand instantly (no refetch).
 *
 * Lazy-load: when fetchChildrenUrl is provided and a node's children have not
 * yet been fetched, the expand click fires a GET request to that URL with
 * ?parent_id=<id>.  The admin tree never receives fetchChildrenUrl, so it
 * always renders from the full pre-loaded flat list.
 */
import {
  ReactFlow,
  Node,
  Edge,
  Background,
  Controls,
  MiniMap,
  Panel,
  ReactFlowInstance,
  useNodesState,
  useEdgesState,
  BackgroundVariant,
  Handle,
  Position,
} from '@xyflow/react'
import '@xyflow/react/dist/style.css'
import { useMemo, useEffect, useState, useCallback } from 'react'
import { CheckCircle, Clock, ChevronDown, ChevronRight, Loader2 } from 'lucide-react'

// ─── Types ────────────────────────────────────────────────────────────────────

export interface TreeAgentNode {
  id: number
  name: string
  email: string
  agent_type: string
  onboarding_status: string
  parent_agent_id: number | null
  sub_agent_count: number
  // Admin-only fields (optional)
  override_percentage?: number
  commission_total?: number
  equity_credits?: number
  city?: string | null
  state?: string | null
  // Agent-view field
  contract_signed?: boolean
}

interface AgentTreeViewProps {
  agents: TreeAgentNode[]
  showFinancials?: boolean
  /**
   * When provided, nodes with unloaded children will fire a GET request to
   * `${fetchChildrenUrl}?parent_id=<id>` on expand.  Omit for the admin tree
   * (all data is pre-loaded).
   */
  fetchChildrenUrl?: string
  /**
   * When set, nodes at or below this depth (root = depth 0) start collapsed, so
   * a large pre-loaded tree opens at a readable zoom instead of every branch at
   * once.  Omit to show everything that is loaded (used by the lazy-loading
   * agent view, whose payload is already depth-capped server-side).
   */
  defaultExpandDepth?: number
}

// ─── Status maps ──────────────────────────────────────────────────────────────

const statusColors: Record<string, { bg: string; text: string; label: string }> = {
  active:            { bg: 'bg-green-100',  text: 'text-green-700',  label: 'Active' },
  prospect:          { bg: 'bg-gray-100',   text: 'text-gray-600',   label: 'Prospect' },
  invited:           { bg: 'bg-blue-100',   text: 'text-blue-700',   label: 'Invited' },
  contract_pending:  { bg: 'bg-yellow-100', text: 'text-yellow-700', label: 'Contract Pending' },
  documents_pending: { bg: 'bg-orange-100', text: 'text-orange-700', label: 'Docs Pending' },
  training_pending:  { bg: 'bg-purple-100', text: 'text-purple-700', label: 'Training' },
  inactive:          { bg: 'bg-red-100',    text: 'text-red-600',    label: 'Inactive' },
}

const cardStyles: Record<string, { border: string; headerBg: string; headerText: string; subtitleText: string; bodyBg: string; opacity: string }> = {
  active: {
    border: 'border-chestnut-forest',
    headerBg: 'bg-chestnut-forest',
    headerText: 'text-white',
    subtitleText: 'text-white/70',
    bodyBg: 'bg-white',
    opacity: '',
  },
  inactive: {
    border: 'border-red-300',
    headerBg: 'bg-red-400',
    headerText: 'text-white',
    subtitleText: 'text-white/70',
    bodyBg: 'bg-red-50/50',
    opacity: 'opacity-75',
  },
  invited: {
    border: 'border-blue-300',
    headerBg: 'bg-blue-500',
    headerText: 'text-white',
    subtitleText: 'text-white/70',
    bodyBg: 'bg-blue-50/50',
    opacity: '',
  },
  prospect: {
    border: 'border-gray-300',
    headerBg: 'bg-gray-400',
    headerText: 'text-white',
    subtitleText: 'text-white/70',
    bodyBg: 'bg-gray-50/50',
    opacity: 'opacity-80',
  },
  contract_pending: {
    border: 'border-yellow-300',
    headerBg: 'bg-yellow-500',
    headerText: 'text-white',
    subtitleText: 'text-white/70',
    bodyBg: 'bg-yellow-50/50',
    opacity: '',
  },
  documents_pending: {
    border: 'border-orange-300',
    headerBg: 'bg-orange-500',
    headerText: 'text-white',
    subtitleText: 'text-white/70',
    bodyBg: 'bg-orange-50/50',
    opacity: '',
  },
  training_pending: {
    border: 'border-purple-300',
    headerBg: 'bg-purple-500',
    headerText: 'text-white',
    subtitleText: 'text-white/70',
    bodyBg: 'bg-purple-50/50',
    opacity: '',
  },
}

const defaultCardStyle = cardStyles.active

function getCardStyle(status: string, isRoot: boolean) {
  if (isRoot && status === 'active') return cardStyles.active
  return cardStyles[status] || defaultCardStyle
}

// ─── Layout constants ─────────────────────────────────────────────────────────

/**
 * The exact rendered width of a node card, in pixels.  The card is pinned to
 * this width (never min/max) so the layout builder can reserve a slot that the
 * card can never outgrow — that is what keeps siblings from colliding.
 */
export const NODE_WIDTH = 260
/** Horizontal gutter between two sibling subtrees. */
export const H_GAP = 48
/** Vertical gutter between the bottom of one level and the top of the next. */
export const V_GAP = 90

// Vertical building blocks of a card, matching the markup below.  Used to
// estimate a card's height so each level can be offset by the tallest card it
// actually contains instead of one hard-coded number.
const CARD_BORDER = 4    // border-2, top + bottom
const HEADER_H    = 52   // px-4 py-2 + name (20) + subtitle (16)
const BODY_PAD    = 24   // py-3, top + bottom
const ROW_GAP     = 8    // space-y-2
const STATUS_ROW  = 20   // status pill row
const TEXT_ROW    = 18   // any text-xs detail row
const TOGGLE_ROW  = 28   // mt-1 + py-1 button

/**
 * Estimated rendered height of a card for the given agent, in pixels.
 * Admin cards (financials + override) are taller than agent cards, so this is
 * evaluated per node and maxed per level by the layout builder.
 */
export function estimateNodeHeight(agent: TreeAgentNode, showFinancials: boolean): number {
  const rows: number[] = [STATUS_ROW]

  if (agent.contract_signed !== undefined) rows.push(TEXT_ROW)
  if (showFinancials) rows.push(TEXT_ROW, TEXT_ROW)          // commissions + equity credits
  if (agent.sub_agent_count > 0) rows.push(TEXT_ROW)          // sponsored agents
  if (showFinancials && (agent.override_percentage ?? 0) > 0) rows.push(TEXT_ROW)
  if (agent.sub_agent_count > 0) rows.push(TOGGLE_ROW)

  const body = rows.reduce((sum, r) => sum + r, 0) + ROW_GAP * (rows.length - 1)
  return CARD_BORDER + HEADER_H + BODY_PAD + body
}

// ─── Node component ───────────────────────────────────────────────────────────

interface NodeData extends TreeAgentNode {
  isRoot: boolean
  showFinancials: boolean
  /** True when sub_agent_count > 0 but children have not been fetched yet */
  hasUnloadedChildren: boolean
  /** True when the node's children are loaded but hidden */
  isCollapsed: boolean
  /** True when a fetch is in flight for this node's children */
  isLoading: boolean
  onToggle: (id: number) => void
}

function AgentNodeComponent({ data }: { data: NodeData }) {
  const status       = statusColors[data.onboarding_status] || statusColors.active
  const style        = getCardStyle(data.onboarding_status, data.isRoot)
  const isActive     = data.onboarding_status === 'active'
  const sponsoredStyle = !data.isRoot && isActive

  const hasChildren  = data.sub_agent_count > 0
  const toggleLabel  = data.isCollapsed || data.hasUnloadedChildren ? 'Expand' : 'Collapse'

  return (
    <div
      style={{ width: NODE_WIDTH }}
      className={`box-border rounded-xl shadow-lg border-2 ${style.opacity} ${
        sponsoredStyle
          ? 'border-chestnut-gold/50 bg-white'
          : `${style.border} ${style.bodyBg}`
      }`}
    >
      <Handle
        type="target"
        position={Position.Top}
        className="!bg-chestnut-forest !w-3 !h-3 !border-2 !border-white"
      />

      {/* Header */}
      <div
        className={`px-4 py-2 rounded-t-[10px] ${
          sponsoredStyle
            ? 'bg-chestnut-gold/10 text-chestnut-charcoal'
            : `${style.headerBg} ${style.headerText}`
        }`}
      >
        <div className="font-semibold text-sm truncate" title={data.name}>{data.name}</div>
        <div className={`text-xs ${sponsoredStyle ? 'text-chestnut-charcoal/50' : style.subtitleText}`}>
          {data.sub_agent_count > 0 ? 'Sponsor' : 'Agent'}
        </div>
      </div>

      {/* Body */}
      <div className="px-4 py-3 space-y-2">
        {/* Status + location */}
        <div className="flex items-center justify-between gap-2 min-w-0">
          <span
            className={`flex-shrink-0 px-2 py-0.5 text-[10px] font-medium rounded-full whitespace-nowrap ${status.bg} ${status.text}`}
          >
            {status.label}
          </span>
          {data.state && (
            <span
              className="text-[10px] text-chestnut-charcoal/50 truncate min-w-0"
              title={`${data.city ? `${data.city}, ` : ''}${data.state}`}
            >
              {data.city ? `${data.city}, ` : ''}{data.state}
            </span>
          )}
        </div>

        {/* Agreement-signed indicator (agent view) */}
        {data.contract_signed !== undefined && (
          <div className="flex items-center gap-1.5 text-xs">
            {data.contract_signed ? (
              <>
                <CheckCircle className="h-3.5 w-3.5 text-green-600 flex-shrink-0" />
                <span className="text-green-700 font-medium">Agreement signed</span>
              </>
            ) : (
              <>
                <Clock className="h-3.5 w-3.5 text-amber-500 flex-shrink-0" />
                <span className="text-amber-700 font-medium">Agreement pending</span>
              </>
            )}
          </div>
        )}

        {/* Financial rows (admin view only). Each row also checks that its value
            was actually served — when the organization hides commissions and
            equity the fields are absent, and a missing field must not render as
            $0.00 or a blank row. */}
        {data.showFinancials && data.commission_total !== undefined && (
          <div className="flex items-center justify-between gap-2 text-xs min-w-0">
            <span className="text-chestnut-charcoal/60 flex-shrink-0">Commissions</span>
            <span
              className="font-semibold text-chestnut-forest truncate min-w-0"
              title={`$${data.commission_total.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
            >
              ${data.commission_total.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </span>
          </div>
        )}
        {data.showFinancials && data.equity_credits !== undefined && (
          <div className="flex items-center justify-between gap-2 text-xs min-w-0">
            <span className="text-chestnut-charcoal/60 flex-shrink-0">Equity Credits</span>
            <span
              className="font-semibold text-chestnut-burgundy truncate min-w-0"
              title={data.equity_credits.toLocaleString()}
            >
              {data.equity_credits.toLocaleString()}
            </span>
          </div>
        )}

        {data.sub_agent_count > 0 && (
          <div className="flex items-center justify-between gap-2 text-xs min-w-0">
            <span className="text-chestnut-charcoal/60 flex-shrink-0">Sponsored Agents</span>
            <span className="font-semibold text-chestnut-gold truncate min-w-0">{data.sub_agent_count}</span>
          </div>
        )}

        {data.showFinancials && (data.override_percentage ?? 0) > 0 && (
          <div className="flex items-center justify-between gap-2 text-xs min-w-0">
            <span className="text-chestnut-charcoal/60 flex-shrink-0">Override</span>
            <span className="font-semibold text-chestnut-charcoal truncate min-w-0">{data.override_percentage}%</span>
          </div>
        )}

        {/* Expand / collapse button */}
        {hasChildren && (
          <button
            onClick={() => data.onToggle(data.id)}
            disabled={data.isLoading}
            aria-label={toggleLabel}
            className={`w-full mt-1 flex items-center justify-center gap-1 text-[10px] font-medium rounded-md py-1 transition-colors
              ${data.isLoading
                ? 'text-chestnut-charcoal/40 cursor-not-allowed'
                : 'text-chestnut-charcoal/60 hover:text-chestnut-forest hover:bg-chestnut-forest/5 cursor-pointer'
              }`}
          >
            {data.isLoading ? (
              <>
                <Loader2 className="h-3 w-3 animate-spin" />
                <span>Loading…</span>
              </>
            ) : data.isCollapsed || data.hasUnloadedChildren ? (
              <>
                <ChevronRight className="h-3 w-3" />
                <span>Show {data.sub_agent_count} sub-agent{data.sub_agent_count !== 1 ? 's' : ''}</span>
              </>
            ) : (
              <>
                <ChevronDown className="h-3 w-3" />
                <span>Collapse</span>
              </>
            )}
          </button>
        )}
      </div>

      <Handle
        type="source"
        position={Position.Bottom}
        className="!bg-chestnut-gold !w-3 !h-3 !border-2 !border-white"
      />
    </div>
  )
}

// nodeTypes must be stable (defined outside component) so ReactFlow doesn't
// remount nodes on every render. Callbacks are delivered through node.data.
const nodeTypes = { agentNode: AgentNodeComponent }

// ─── Layout builder ───────────────────────────────────────────────────────────

/**
 * Build ReactFlow nodes and edges from a flat agent list.
 *
 * collapsedIds: nodes whose subtrees are hidden (children kept in allAgents
 *               but excluded from the layout).
 * loadingIds:   nodes currently being fetched (spinner shown, button disabled).
 * fetchedParentIds: nodes whose children have already been retrieved from the
 *               server (so we know not to fire another request).
 * canLazyLoad:  true when fetchChildrenUrl is provided.
 */
export function buildTree(
  agents: TreeAgentNode[],
  showFinancials: boolean,
  collapsedIds: Set<number>,
  loadingIds: Set<number>,
  fetchedParentIds: Set<number>,
  canLazyLoad: boolean,
  onToggle: (id: number) => void
): { nodes: Node[]; edges: Edge[] } {
  const nodes: Node[] = []
  const edges: Edge[] = []

  const byId        = new Map<number, TreeAgentNode>()
  const childrenMap = new Map<number, TreeAgentNode[]>()

  agents.forEach((a) => { if (!byId.has(a.id)) byId.set(a.id, a) })

  // A child whose sponsor is missing from the payload (or points at itself)
  // becomes a root of its own rather than disappearing from the tree.
  const rootAgents: TreeAgentNode[] = []
  byId.forEach((a) => {
    const parentId = a.parent_agent_id
    if (parentId && parentId !== a.id && byId.has(parentId)) {
      const siblings = childrenMap.get(parentId)
      if (siblings) siblings.push(a)
      else childrenMap.set(parentId, [a])
    } else {
      rootAgents.push(a)
    }
  })

  const childrenOf = (id: number) => childrenMap.get(id) || []

  const hasUnloadedChildrenFor = (agent: TreeAgentNode) =>
    canLazyLoad &&
    agent.sub_agent_count > 0 &&
    !fetchedParentIds.has(agent.id) &&
    childrenOf(agent.id).length === 0

  /** Children that are actually drawn beneath this node right now. */
  const visibleChildrenOf = (agent: TreeAgentNode) =>
    collapsedIds.has(agent.id) || hasUnloadedChildrenFor(agent) ? [] : childrenOf(agent.id)

  // A sponsor cycle (a → b → a) leaves its members reachable from no root at
  // all. Promote the first such member to a root so those agents still appear.
  const reachable = new Set<number>()
  function markReachable(agent: TreeAgentNode) {
    if (reachable.has(agent.id)) return
    reachable.add(agent.id)
    childrenOf(agent.id).forEach(markReachable)
  }
  rootAgents.forEach(markReachable)
  byId.forEach((a) => {
    if (reachable.has(a.id)) return
    rootAgents.push(a)
    markReachable(a)
  })

  // ── Pass 1: walk the visible tree to learn each node's depth, guarding
  //            against cycles in the sponsor chain.
  const visited = new Set<number>()
  const ordered: { agent: TreeAgentNode; depth: number }[] = []

  function walk(agent: TreeAgentNode, depth: number) {
    if (visited.has(agent.id)) return
    visited.add(agent.id)
    ordered.push({ agent, depth })
    visibleChildrenOf(agent).forEach((child) => walk(child, depth + 1))
  }
  rootAgents.forEach((root) => walk(root, 0))

  // ── Pass 2: each level is as tall as the tallest card it contains, so admin
  //            cards (financials + override) never crowd the level below.
  const levelHeights: number[] = []
  ordered.forEach(({ agent, depth }) => {
    const h = estimateNodeHeight(agent, showFinancials)
    levelHeights[depth] = Math.max(levelHeights[depth] ?? 0, h)
  })

  const levelY: number[] = []
  for (let depth = 0; depth < levelHeights.length; depth++) {
    levelY[depth] = depth === 0 ? 0 : levelY[depth - 1] + (levelHeights[depth - 1] ?? 0) + V_GAP
  }

  // ── Pass 3: reserve a horizontal slot per subtree, then centre each parent
  //            over its children.  Every card is exactly NODE_WIDTH wide, so a
  //            subtree can never spill outside its own slot.
  const widthCache = new Map<number, number>()

  function getSubtreeWidth(agent: TreeAgentNode): number {
    const cached = widthCache.get(agent.id)
    if (cached !== undefined) return cached
    // Placed first to stop a cycle from recursing forever.
    widthCache.set(agent.id, NODE_WIDTH)

    const children = visibleChildrenOf(agent).filter((c) => visited.has(c.id))
    if (children.length === 0) return NODE_WIDTH

    const childrenWidth = children.reduce((sum, c) => sum + getSubtreeWidth(c), 0)
    const w = Math.max(NODE_WIDTH, childrenWidth + H_GAP * (children.length - 1))
    widthCache.set(agent.id, w)
    return w
  }

  const placed = new Set<number>()

  function placeNode(agent: TreeAgentNode, x: number, depth: number, isRoot: boolean) {
    if (placed.has(agent.id)) return
    placed.add(agent.id)

    const isCollapsed         = collapsedIds.has(agent.id)
    const isLoading           = loadingIds.has(agent.id)
    const hasUnloadedChildren = hasUnloadedChildrenFor(agent)

    nodes.push({
      id:        String(agent.id),
      type:      'agentNode',
      position:  { x, y: levelY[depth] ?? 0 },
      draggable: false,
      width:     NODE_WIDTH,
      data:      {
        ...agent,
        isRoot,
        showFinancials,
        isCollapsed,
        isLoading,
        hasUnloadedChildren,
        onToggle,
        // Exposed for layout assertions; the card itself sizes from its content.
        layoutWidth:  NODE_WIDTH,
        layoutHeight: estimateNodeHeight(agent, showFinancials),
      } as unknown as Record<string, unknown>,
    })

    const children = visibleChildrenOf(agent).filter((c) => !placed.has(c.id))
    if (children.length === 0) return

    const totalWidth = children.reduce((sum, c) => sum + getSubtreeWidth(c), 0) +
                       H_GAP * (children.length - 1)
    let currentX = x + NODE_WIDTH / 2 - totalWidth / 2

    children.forEach((child) => {
      const subtreeWidth = getSubtreeWidth(child)
      const childX       = currentX + subtreeWidth / 2 - NODE_WIDTH / 2

      const edgeStyle =
        child.onboarding_status === 'inactive'
          ? { stroke: '#f87171', strokeWidth: 2, strokeDasharray: '6 3' }
          : child.onboarding_status === 'invited'
          ? { stroke: '#3b82f6', strokeWidth: 2, strokeDasharray: '6 3' }
          : { stroke: '#C9A962', strokeWidth: 2 }

      edges.push({
        id:     `e${agent.id}-${child.id}`,
        source: String(agent.id),
        target: String(child.id),
        // Orthogonal elbow routing reads as a hierarchy even with many
        // children; nothing animates, so a wide fan-out stays legible.
        type:     'smoothstep',
        pathOptions: { borderRadius: 8 },
        style:    edgeStyle,
        animated: false,
      } as Edge)

      placeNode(child, childX, depth + 1, false)
      currentX += subtreeWidth + H_GAP
    })
  }

  let currentRootX = 0
  rootAgents.forEach((root) => {
    const subtreeWidth = getSubtreeWidth(root)
    placeNode(root, currentRootX + subtreeWidth / 2 - NODE_WIDTH / 2, 0, true)
    currentRootX += subtreeWidth + H_GAP * 2
  })

  return { nodes, edges }
}

/**
 * IDs of nodes that should start collapsed so a large pre-loaded tree opens at
 * a readable zoom: everything at or below `depth` (root = depth 0) that has
 * children.  Expanding one of them reveals the next level, still collapsed.
 */
export function collapsedIdsBeyondDepth(agents: TreeAgentNode[], depth: number): Set<number> {
  const collapsed = new Set<number>()
  if (depth < 0) return collapsed

  const byId        = new Map<number, TreeAgentNode>()
  const childrenMap = new Map<number, TreeAgentNode[]>()
  agents.forEach((a) => { if (!byId.has(a.id)) byId.set(a.id, a) })

  const roots: TreeAgentNode[] = []
  byId.forEach((a) => {
    const parentId = a.parent_agent_id
    if (parentId && parentId !== a.id && byId.has(parentId)) {
      const siblings = childrenMap.get(parentId)
      if (siblings) siblings.push(a)
      else childrenMap.set(parentId, [a])
    } else {
      roots.push(a)
    }
  })

  const seen = new Set<number>()
  function walk(agent: TreeAgentNode, d: number) {
    if (seen.has(agent.id)) return
    seen.add(agent.id)
    const children = childrenMap.get(agent.id) || []
    if (d >= depth && (children.length > 0 || agent.sub_agent_count > 0)) collapsed.add(agent.id)
    children.forEach((c) => walk(c, d + 1))
  }
  roots.forEach((r) => walk(r, 0))

  return collapsed
}

/** IDs of every loaded node that has children — used by "Collapse all". */
export function allParentIds(agents: TreeAgentNode[]): Set<number> {
  const ids = new Set<number>()
  const present = new Set(agents.map((a) => a.id))
  agents.forEach((a) => {
    if (a.parent_agent_id && a.parent_agent_id !== a.id && present.has(a.parent_agent_id)) {
      ids.add(a.parent_agent_id)
    }
  })
  return ids
}

// ─── Main component ───────────────────────────────────────────────────────────

export default function AgentTreeView({
  agents,
  showFinancials = false,
  fetchChildrenUrl,
  defaultExpandDepth,
}: AgentTreeViewProps) {
  // Full agent list: starts with the prop, grows as branches are lazy-loaded.
  const [allAgents, setAllAgents]             = useState<TreeAgentNode[]>(agents)
  // IDs of nodes whose subtree is hidden (children remain in allAgents).
  const [collapsedIds, setCollapsedIds]       = useState<Set<number>>(
    () => (defaultExpandDepth === undefined ? new Set() : collapsedIdsBeyondDepth(agents, defaultExpandDepth))
  )
  // IDs currently being fetched from the server.
  const [loadingIds, setLoadingIds]           = useState<Set<number>>(new Set())
  // IDs whose children have already been fetched (don't re-fetch on expand).
  const [fetchedParentIds, setFetchedParentIds] = useState<Set<number>>(new Set())
  // Minimap is dismissible so it can't sit on top of the cards behind it.
  const [showMiniMap, setShowMiniMap]         = useState(true)
  const [flow, setFlow]                       = useState<ReactFlowInstance | null>(null)

  const canLazyLoad = Boolean(fetchChildrenUrl)

  // Sync when the agents prop is replaced (e.g. Inertia navigation).
  useEffect(() => {
    setAllAgents(agents)
    setCollapsedIds(
      defaultExpandDepth === undefined ? new Set() : collapsedIdsBeyondDepth(agents, defaultExpandDepth)
    )
    setLoadingIds(new Set())
    setFetchedParentIds(new Set())
  }, [agents, defaultExpandDepth])

  // Toggle handler — stable across renders via useCallback.
  const handleToggle = useCallback(
    (id: number) => {
      // Guard: don't act while a fetch is in flight for this node.
      if (loadingIds.has(id)) return

      // Already collapsed → expand instantly (children are in allAgents).
      if (collapsedIds.has(id)) {
        setCollapsedIds((prev) => { const s = new Set(prev); s.delete(id); return s })
        return
      }

      // Check whether this node's children are already in allAgents.
      const childrenPresent = allAgents.some((a) => a.parent_agent_id === id)

      if (!canLazyLoad || childrenPresent) {
        // Collapse — children are loaded, just hide them.
        setCollapsedIds((prev) => new Set(prev).add(id))
        return
      }

      // Need to lazy-load children from the server.
      setLoadingIds((prev) => new Set(prev).add(id))
      fetch(`${fetchChildrenUrl}?parent_id=${id}`, {
        headers: { Accept: 'application/json' },
        credentials: 'same-origin',
      })
        .then((res) => {
          if (!res.ok) throw new Error(`HTTP ${res.status}`)
          return res.json() as Promise<{ nodes: TreeAgentNode[] }>
        })
        .then(({ nodes: newNodes }) => {
          // Merge new nodes; deduplicate by id in case of concurrent fetches.
          setAllAgents((prev) => {
            const existingIds = new Set(prev.map((a) => a.id))
            const fresh = newNodes.filter((n) => !existingIds.has(n.id))
            return [...prev, ...fresh]
          })
          setFetchedParentIds((prev) => new Set(prev).add(id))
          setLoadingIds((prev) => { const s = new Set(prev); s.delete(id); return s })
        })
        .catch(() => {
          // On error, clear loading state so the user can retry.
          setLoadingIds((prev) => { const s = new Set(prev); s.delete(id); return s })
        })
    },
    [loadingIds, collapsedIds, allAgents, canLazyLoad, fetchChildrenUrl]
  )

  // Recompute layout whenever anything relevant changes.
  const { computedNodes, computedEdges } = useMemo(() => {
    const { nodes, edges } = buildTree(
      allAgents,
      showFinancials,
      collapsedIds,
      loadingIds,
      fetchedParentIds,
      canLazyLoad,
      handleToggle
    )
    return { computedNodes: nodes, computedEdges: edges }
  }, [allAgents, showFinancials, collapsedIds, loadingIds, fetchedParentIds, canLazyLoad, handleToggle])

  const [nodes, setNodes, onNodesChange] = useNodesState(computedNodes)
  const [edges, setEdges, onEdgesChange] = useEdgesState(computedEdges)

  useEffect(() => {
    setNodes(computedNodes)
    setEdges(computedEdges)
  }, [computedNodes, computedEdges])

  const fitView = useCallback(() => {
    flow?.fitView({ padding: 0.2, duration: 300 })
  }, [flow])

  const expandAll   = useCallback(() => setCollapsedIds(new Set()), [])
  const collapseAll = useCallback(() => setCollapsedIds(allParentIds(allAgents)), [allAgents])
  const resetView   = useCallback(() => {
    setCollapsedIds(
      defaultExpandDepth === undefined ? new Set() : collapsedIdsBeyondDepth(allAgents, defaultExpandDepth)
    )
    fitView()
  }, [allAgents, defaultExpandDepth, fitView])

  const controlButton =
    'px-2 py-1 text-xs font-medium rounded-md border border-chestnut-charcoal/10 bg-white ' +
    'text-chestnut-charcoal/70 hover:text-chestnut-forest hover:bg-chestnut-forest/5 shadow-sm'

  return (
    <ReactFlow
      nodes={nodes}
      edges={edges}
      onNodesChange={onNodesChange}
      onEdgesChange={onEdgesChange}
      onInit={setFlow}
      nodeTypes={nodeTypes}
      fitView
      fitViewOptions={{ padding: 0.2 }}
      minZoom={0.1}
      maxZoom={2}
      // The computed layout is authoritative — cards can't be dragged out of line.
      nodesDraggable={false}
      nodesConnectable={false}
      elementsSelectable={false}
      attributionPosition="top-left"
    >
      <Background variant={BackgroundVariant.Dots} gap={20} size={1} color="#2D2D2D20" />

      <Panel position="top-right" className="flex flex-wrap items-center gap-1.5">
        <button type="button" onClick={expandAll} className={controlButton}>Expand all</button>
        <button type="button" onClick={collapseAll} className={controlButton}>Collapse all</button>
        <button type="button" onClick={resetView} className={controlButton}>Reset view</button>
        <button
          type="button"
          onClick={() => setShowMiniMap((v) => !v)}
          aria-pressed={showMiniMap}
          className={controlButton}
        >
          {showMiniMap ? 'Hide map' : 'Show map'}
        </button>
      </Panel>

      <Controls
        position="bottom-left"
        showInteractive={false}
        className="!bg-white !border-chestnut-charcoal/10 !shadow-md !rounded-lg"
      />

      {showMiniMap && (
        <MiniMap
          position="bottom-right"
          pannable
          zoomable
          style={{ width: 168, height: 112 }}
          nodeColor={(node) => {
            const data = node.data as unknown as TreeAgentNode & { isRoot: boolean }
            const s    = data.onboarding_status
            if (s === 'inactive')          return '#f87171'
            if (s === 'invited')           return '#3b82f6'
            if (s === 'prospect')          return '#9ca3af'
            if (s === 'contract_pending')  return '#eab308'
            if (s === 'documents_pending') return '#f97316'
            if (s === 'training_pending')  return '#a855f7'
            return data.isRoot ? '#1A3C34' : '#C9A962'
          }}
          className="!bg-chestnut-cream !border-chestnut-charcoal/10 !rounded-lg"
          maskColor="rgba(26, 60, 52, 0.1)"
        />
      )}
    </ReactFlow>
  )
}
